import { createContext, useContext } from 'react';
import type { LoadedDocument } from './session';
import type { AutosaveRecord } from './autosave';
import type { SubtitleDocument } from '../../core';

/**
 * Shared document session contract. Kept in a component-free module so the
 * Provider's file can satisfy react-refresh's "components only" rule while the
 * hook and context object live here.
 */
/**
 * How a {@link SessionValue.load} request resolved:
 * - `'loaded'` — the file was parsed and is now the active document;
 * - `'parked'` — unsaved edits parked the load behind the discard-edits
 *   ConfirmDialog (it may still load later, but has NOT loaded yet);
 * - `'error'` — the load failed (unknown format / too large / parse throw) and
 *   the session `error` state was set;
 * - `'pro-locked'` — a free user's Pro-format import was gated and
 *   `proImportLocked` was set.
 *
 * Callers that confirm a load ("Opened X…") must only claim it on `'loaded'` —
 * the other branches surface their own UX (confirm dialog / error / upgrade
 * prompt), and claiming success there would be a lie.
 */
export type LoadOutcome = 'loaded' | 'parked' | 'error' | 'pro-locked';

export interface SessionValue {
  loaded: LoadedDocument | null;
  /** Error from the most recent failed load (e.g. unknown format), else null. */
  error: string | null;
  /** Name of a Pro format a free user tried to import (gated), else null. */
  proImportLocked: string | null;
  /**
   * Read a File, decode/parse/repair it, and make it the active document.
   * `allowPro` permits Pro-format imports (SCC/STL/TTML) when the user is licensed.
   * Resolves with what actually happened (see {@link LoadOutcome}).
   */
  load: (file: File, allowPro?: boolean) => Promise<LoadOutcome>;
  /** Replace the active document's canonical model, pushing an undo step. */
  setDoc: (doc: SubtitleDocument) => void;
  /**
   * Load a document that was minted in-app rather than read from a file — e.g.
   * transcription's EMPTY-START output (cue-forge-sdv.11.4), the app's first
   * doc-creating feature. Becomes the active document with no filename/import
   * provenance and no undo history, mirroring `load`.
   *
   * **Contract: EMPTY-only.** Unlike `load`, this has no dirty/confirm guard
   * of its own — it is a no-op when a document is already loaded, rather than
   * silently replacing it. Only call it when `loaded === null`; replacing an
   * *existing* document goes through `setDoc` instead, so that stays undoable.
   */
  loadFreshDocument: (doc: SubtitleDocument) => void;
  /**
   * Persist the QC accept keys (cue-forge-n3e.7) on the loaded document. Not a
   * document edit — no undo step, no dirty flag, the `doc` reference is kept —
   * so it rides the autosave without disturbing QC's doc-keyed auto-rerun.
   */
  setQcAccepted: (keys: string[]) => void;
  /** Restore the previous document state (no-op if there is none). */
  undo: () => void;
  /** Re-apply an undone document state (no-op if there is none). */
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  /** True when the document has been edited since load or last export. */
  dirty: boolean;
  /** Mark the document exported: clears `dirty` until the next mutation. */
  markClean: () => void;
  clear: () => void;
  /** A locally-autosaved session found on mount, offered for restore (else null). */
  restorable: AutosaveRecord | null;
  /** Restore the autosaved session as the active document. */
  restore: () => void;
  /** Dismiss the restore offer and delete the local autosave. */
  discardRestore: () => void;
  /** Whether local autosave is on (off forgets the local copy — for confidential work). */
  autosaveEnabled: boolean;
  setAutosaveEnabled: (on: boolean) => void;
}

export const SessionContext = createContext<SessionValue | null>(null);

/** Access the active document session. Must be used within a SessionProvider. */
export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within a SessionProvider');
  return ctx;
}
